import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Timestamp } from "../../google/protobuf/timestamp";
export const protobufPackage = "com.snap.camerakit.v3";
export interface LegalPrompt {
    documents: LegalDocument[];
    disabled: boolean;
}
export interface LegalDocument {
    type: LegalDocument_Type;
    webUrl: string;
    version: string;
    timestamp: Date | undefined;
}
export enum LegalDocument_Type {
    UNSET = 0,
    TERMS_OF_SERVICE = 1,
    PRIVACY_POLICY = 2,
    LEARN_MORE = 3,
    UNRECOGNIZED = -1
}
function createBaseLegalPrompt(): LegalPrompt {
    return { documents: [], disabled: false };
}
export const LegalPrompt: MessageFns<LegalPrompt> = {
    encode(message: LegalPrompt, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        for (const v of message.documents) {
            LegalDocument.encode(v!, writer.uint32(10).fork()).join();
        }
        if (message.disabled !== false) {
            writer.uint32(16).bool(message.disabled);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): LegalPrompt {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseLegalPrompt();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.documents.push(LegalDocument.decode(reader, reader.uint32()));
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.disabled = reader.bool();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<LegalPrompt>, I>>(base?: I): LegalPrompt {
        return LegalPrompt.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<LegalPrompt>, I>>(object: I): LegalPrompt {
        const message = createBaseLegalPrompt();
        message.documents = object.documents?.map((e) => LegalDocument.fromPartial(e)) || [];
        message.disabled = object.disabled ?? false;
        return message;
    },
};
function createBaseLegalDocument(): LegalDocument {
    return { type: 0, webUrl: "", version: "", timestamp: undefined };
}
export const LegalDocument: MessageFns<LegalDocument> = {
    encode(message: LegalDocument, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.type !== 0) {
            writer.uint32(8).int32(message.type);
        }
        if (message.webUrl !== "") {
            writer.uint32(18).string(message.webUrl);
        }
        if (message.version !== "") {
            writer.uint32(26).string(message.version);
        }
        if (message.timestamp !== undefined) {
            Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(34).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): LegalDocument {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseLegalDocument();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.type = reader.int32() as any;
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.webUrl = reader.string();
                    continue;
                }
                case 3: {
                    if (tag !== 26) {
                        break;
                    }
                    message.version = reader.string();
                    continue;
                }
                case 4: {
                    if (tag !== 34) {
                        break;
                    }
                    message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<LegalDocument>, I>>(base?: I): LegalDocument {
        return LegalDocument.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<LegalDocument>, I>>(object: I): LegalDocument {
        const message = createBaseLegalDocument();
        message.type = object.type ?? 0;
        message.webUrl = object.webUrl ?? "";
        message.version = object.version ?? "";
        message.timestamp = object.timestamp ?? undefined;
        return message;
    },
};
type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;
export type DeepPartial<T> = T extends Builtin ? T : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>> : T extends {
    $case: string;
} ? {
    [K in keyof Omit<T, "$case">]?: DeepPartial<T[K]>;
} & {
    $case: T["$case"];
} : T extends {} ? {
    [K in keyof T]?: DeepPartial<T[K]>;
} : Partial<T>;
type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P : P & {
    [K in keyof P]: Exact<P[K], I[K]>;
} & {
    [K in Exclude<keyof I, KeysOfUnion<P>>]: never;
};
function toTimestamp(date: Date): Timestamp {
    const seconds = Math.trunc(date.getTime() / 1000).toString();
    const nanos = (date.getTime() % 1000) * 1000000;
    return { seconds, nanos };
}
function fromTimestamp(t: Timestamp): Date {
    let millis = (globalThis.Number(t.seconds) || 0) * 1000;
    millis += (t.nanos || 0) / 1000000;
    return new globalThis.Date(millis);
}
export interface MessageFns<T> {
    encode(message: T, writer?: BinaryWriter): BinaryWriter;
    decode(input: BinaryReader | Uint8Array, length?: number): T;
    create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
    fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
